import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["form"];

  connect() {
    this.initializeForm();
    this.isNavigating = false;
    this.isGoingForward = false;
    this.isSubmitting = false;

    // Bind methods
    this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
    this.handleTurboVisit = this.handleTurboVisit.bind(this);
    this.handlePopState = this.handlePopState.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);

    // Add event listeners
    window.addEventListener("beforeunload", this.handleBeforeUnload);
    document.addEventListener("turbo:before-visit", this.handleTurboVisit);
    window.addEventListener("popstate", this.handlePopState, true);
    this.formTarget.addEventListener("submit", this.handleSubmit);
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    document.removeEventListener("turbo:before-visit", this.handleTurboVisit);
    window.removeEventListener("popstate", this.handlePopState, true);
    this.formTarget.removeEventListener("submit", this.handleSubmit);
  }

  initializeForm() {
    // Reset and capture initial state after a short delay for Uppy
    if (this.initTimeout) {
      clearTimeout(this.initTimeout);
    }

    this.initTimeout = setTimeout(() => {
      this.initialFormState = this.serializeForm();
      this.isNavigating = false;
    }, 500);
  }

  handlePopState(event) {
    // Prevent handling if we're already processing navigation
    if (this.isNavigating || !event.state?.turbo) {
      return;
    }
    if (this.isGoingForward) {
      this.isGoingForward = false;
      return;
    }

    if (this.hasUnsavedChanges()) {
      // prevent other turbo handlers from running
      event.stopImmediatePropagation();

      if (confirm("Are you sure you want to go back?")) {
        this.isNavigating = true;
        const newURL = new URL(window.location.href);
        Turbo.visit(newURL.toString(), { action: "replace" });
        this.isNavigating = false;
      } else {
        this.isGoingForward = true;
        window.history.forward();
      }
    }
  }

  handleBeforeUnload(event) {
    if (!this.isSubmitting && !this.isNavigating && this.hasUnsavedChanges()) {
      event.preventDefault();
      event.returnValue = "";
      return "";
    }
  }

  handleSubmit() {
    this.isSubmitting = true;
  }

  handleTurboVisit(event) {
    if (!this.isSubmitting && !this.isNavigating && this.hasUnsavedChanges()) {
      if (
        !confirm("You have unsaved changes. Are you sure you want to leave?")
      ) {
        event.preventDefault();
      } else {
        this.isNavigating = true;
      }
    }
  }

  hasUnsavedChanges() {
    if (!this.initialFormState) return false;

    const currentFormState = this.serializeForm();
    return this.initialFormState !== currentFormState;
  }

  serializeForm() {
    if (!this.formTarget) return "";

    const formData = new FormData(this.formTarget);
    return Array.from(formData.entries())
      .filter(([key]) => {
        const excludedFields = [
          "authenticity_token",
          "_method",
          "files[]",
          "auto_capitalize_toggle",
        ];
        return !excludedFields.some((field) => key.includes(field));
      })
      .map(([key, value]) => `${key}=${value}`)
      .sort()
      .join("&");
  }
}
